import type { ResultsMode } from "../schema";
import type { DBConfig } from "../utils/indexeddb";
import type { VerifiedAdaptationCache } from "./cacheValidity";

export const PORTFOLIO_ASSETS_STORE_NAME = "assets" as const;
export const ADAPTATIONS_IDB_STORE_NAME = "adaptations" as const;

const PORTFOLIO_ASSETS_KEY_PATH = "id" as const;
const ADAPTATIONS_IDB_STORE_KEY_PATH = "id" as const;

export type PortfolioCacheStore = typeof PORTFOLIO_ASSETS_STORE_NAME | typeof ADAPTATIONS_IDB_STORE_NAME;

/**
 * Bump when portfolio object stores change so existing DBs run `onupgradeneeded`.
 *
 * Never lower this!
 */
export const PORTFOLIO_CACHE_DB_VERSION = 3;

/** Full schema for each `portfolio_*` DB. */
export const PORTFOLIO_IDB_STORES = [
  { storeName: PORTFOLIO_ASSETS_STORE_NAME, keyPath: PORTFOLIO_ASSETS_KEY_PATH },
  { storeName: ADAPTATIONS_IDB_STORE_NAME, keyPath: ADAPTATIONS_IDB_STORE_KEY_PATH },
] as const;

/**
 * Full results read per IndexedDB page when the map scans the store. A bigger
 * page outlives V8's young generation, so its garbage waits for a full GC.
 */
export const FULL_RESULT_PAGE_SIZE = 10;

export const getPortfolioCacheDbName = (portfolioId: string): string => `portfolio_${portfolioId}`;

export interface PortfolioCacheConfigOptions {
  /** Which of the two stores the config reads and writes. Defaults to the assets store. */
  store?: PortfolioCacheStore;
  /**
   * `simple` results come from a different endpoint (`project_halo`) so it has to be
   * in a different database.
   */
  resultsMode?: ResultsMode;
}

/**
 * The single description of a portfolio's IndexedDB cache.
 *
 * Every reader and writer builds its config here, so the map view, the
 * adaptation pathways and the downloads cannot drift onto separate databases.
 */
export const createPortfolioCacheConfig = (
  portfolioId: string,
  { store = PORTFOLIO_ASSETS_STORE_NAME, resultsMode = "comprehensive" }: PortfolioCacheConfigOptions = {}
): DBConfig => ({
  dbName: getPortfolioCacheDbName(resultsMode === "simple" ? `${portfolioId}_simple` : portfolioId),
  version: PORTFOLIO_CACHE_DB_VERSION,
  stores: PORTFOLIO_IDB_STORES,
  storeName: store,
});

const verifiedCacheKey = ({ dbName, storeName }: DBConfig): string => `${dbName}/${storeName}:verified`;

/** Kept in localStorage: a record in the store itself would be served as an adaptation. */
export const readVerifiedCache = (config: DBConfig): VerifiedAdaptationCache | null => {
  try {
    const stored = localStorage.getItem(verifiedCacheKey(config));
    return stored ? (JSON.parse(stored) as VerifiedAdaptationCache) : null;
  } catch {
    return null;
  }
};

export const writeVerifiedCache = (config: DBConfig, verified: VerifiedAdaptationCache): void => {
  try {
    localStorage.setItem(verifiedCacheKey(config), JSON.stringify(verified));
  } catch {
    // Storage is full or unavailable; the next load verifies again.
  }
};
